import SuspenseLoader from "lib/suspenseLoader/SuspenseLoader";
import React, { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { PATHS } from "configs/routeConfig";

// Defined scopes. These scopes are related to crud actions.
// Naming convention as per data from backend.
// Update when any data is changed from backend api
export const AccessScopes = {
  CREATE: "Create",
  EDIT: "Edit",
  VIEW: "View",
  CHANGE: "Change Status",
  DELETE: "Delete",
  ASSIGN_WORKER: "Assign Worker",
  DOWNLOAD: "Download",
  POST: "Post",
  SURVEY: "Survey",
};

// Permission scopes. these scopes are related to module.
// Names are used as per the backend api role category.
// Add/update/remove any scope as per new module added
// or any module category updated from backend or any module deleted.
export const PERMISSION_SCOPES = {
  Dashboard: "dashboard_view",
  Email_Template: "Email Template",
  Master: "Master",
  Workers: "Workers",
  Users: "Users",
  User: "User",
  Settings: "Settings",
  CMS_Pages: "CMS Pages",
  Content: "Content",
  Profile: "Profile",
  Blog: "Blog",
  Faq: "faq",
  Support: "Support",
  Common_Pages: "Common Pages",
  Role: "Role",
  Club: "Club",
  Brand: "Brand",
  Category: "Category",
  Reports: "Reports",
  MangePromotions: "Manage promotions",
  ManagePoints: "Club Manage Points",
  ManageBenefits: "Manage Benefits",
  StaticPages: "Static Pages",
  BlogCategory: "Blog Category",
  FaqCategory: "FAQ Category",
  Club_Reports: "Club Report",
  Admin_User: "Admin User",
  SubscribersList: "Brand Subscriber",
};

// Route scope. map url route with permission scopes
// Add/update/delete when any new route is added or any route is modified
export const routeScopes = {
  // Dashboard
  [PATHS.DASHBOARD]: { scope: PERMISSION_SCOPES.Dashboard },
  //Statistics
  [`${PATHS.STATISTICS}/${PATHS.WORKER_STATISTICS}`]: {
    scope: PERMISSION_SCOPES.Dashboard,
  },
  // User
  [`${PATHS.USER}/${PATHS.USER_LIST}`]: {
    scope: PERMISSION_SCOPES.User,
  },
  // Admin user
  [`${PATHS.USER}/${PATHS.ADMIN_USER}`]: {
    scope: PERMISSION_SCOPES.Admin_User,
  },
  // workers user
  [`${PATHS.USER}/${PATHS.WORKER_LIST}`]: {
    scope: PERMISSION_SCOPES.Workers,
  },
  [`${PATHS.USER}/${PATHS.WORKER_LIST}/${PATHS.EDIT}`]: {
    scope: PERMISSION_SCOPES.Workers,
  },
  [`${PATHS.USER}/${PATHS.WORKER_LIST}/${PATHS.WORKER}`]: {
    scope: PERMISSION_SCOPES.Workers,
  },
  [`${PATHS.USER}/${PATHS.USER_LIST}/${PATHS.WORKER}`]: {
    scope: PERMISSION_SCOPES.Workers,
  },
  // Static pages
  [`${PATHS.CONTENT}/${PATHS.STATIC_PAGES}`]: {
    scope: PERMISSION_SCOPES.StaticPages,
  },
  // Blogs
  [`${PATHS.CONTENT}/${PATHS.BLOGS}/${PATHS.BLOG_LIST}`]: {
    scope: PERMISSION_SCOPES.Blog,
  },
  [`${PATHS.CONTENT}/${PATHS.BLOGS}/${PATHS.BLOG_CATEGORY}`]: {
    scope: PERMISSION_SCOPES.BlogCategory,
  },
  //Faqs
  [`${PATHS.CONTENT}/${PATHS.FAQS}/${PATHS.FAQS_LIST}`]: {
    scope: PERMISSION_SCOPES.FaqCategory,
  },
  [`${PATHS.CONTENT}/${PATHS.FAQS}/${PATHS.FAQS_CATEGORY}`]: {
    scope: PERMISSION_SCOPES.FaqCategory,
  },
  // Master
  [`${PATHS.MASTER_MANAGEMENT}/${PATHS.CITIES}`]: {
    scope: PERMISSION_SCOPES.Master,
  },
  [`${PATHS.MASTER_MANAGEMENT}/${PATHS.ZONES}`]: {
    scope: PERMISSION_SCOPES.Master,
  },
  [`${PATHS.MASTER_MANAGEMENT}/${PATHS.MUSIC_TYPE}`]: {
    scope: PERMISSION_SCOPES.Master,
  },
  [`${PATHS.MASTER_MANAGEMENT}/${PATHS.DRINK_TYPE}`]: {
    scope: PERMISSION_SCOPES.Master,
  },
  [`${PATHS.MASTER_MANAGEMENT}/${PATHS.OUTFITS}`]: {
    scope: PERMISSION_SCOPES.Master,
  },
  [`${PATHS.MASTER_MANAGEMENT}/${PATHS.PLANS}`]: {
    scope: PERMISSION_SCOPES.Master,
  },
  [`${PATHS.MASTER_MANAGEMENT}/${PATHS.CLUB_TYPE}`]: {
    scope: PERMISSION_SCOPES.Master,
  },
  // category
  [`${PATHS.CATEGORY}/${PATHS.LEVELS}`]: {
    scope: PERMISSION_SCOPES.Category,
  },
  [`${PATHS.CATEGORY}/${PATHS.SUPER_LEVELS}`]: {
    scope: PERMISSION_SCOPES.Category,
  },
  [`${PATHS.CATEGORY}/${PATHS.BENEFITS}`]: {
    scope: PERMISSION_SCOPES.Category,
  },
  [`${PATHS.CATEGORY}/${PATHS.ACTIONS}`]: {
    scope: PERMISSION_SCOPES.Category,
  },
  [`${PATHS.CATEGORY}/${PATHS.ATMOSPHERE_RANGES}`]: {
    scope: PERMISSION_SCOPES.Category,
  },
  [`${PATHS.CATEGORY}/${PATHS.QUE_TYPE}`]: {
    scope: PERMISSION_SCOPES.Category,
  },
  [`${PATHS.CATEGORY}/${PATHS.GENDER_MIX}`]: {
    scope: PERMISSION_SCOPES.Category,
  },
  // Club
  [`${PATHS.CLUB}`]: {
    scope: PERMISSION_SCOPES.Club,
  },
  [`${PATHS.CLUB}/${PATHS.CLUBS_POSTS_LIST}`]: {
    scope: PERMISSION_SCOPES.Club,
  },
  [`${PATHS.CLUB}/${PATHS.VIEW}`]: {
    scope: PERMISSION_SCOPES.Club,
  },
  [`${PATHS.CLUB}/${PATHS.CREATE}`]: {
    scope: PERMISSION_SCOPES.Club,
  },
  [`${PATHS.CLUB}/${PATHS.CLUB_ADMIN_LIST}/${PATHS.CREATE}`]: {
    scope: PERMISSION_SCOPES.Club,
  },
  [`${PATHS.CLUB}/${PATHS.EDIT}`]: {
    scope: PERMISSION_SCOPES.Club,
  },
  [`${PATHS.CLUB}/${PATHS.CLUB_ADMIN_LIST}/${PATHS.VIEW}`]: {
    scope: PERMISSION_SCOPES.Club,
  },
  [`${PATHS.CLUB}/${PATHS.CLUB_ADMIN_LIST}`]: {
    scope: PERMISSION_SCOPES.Club,
  },
  // brand
  [`${PATHS.BRAND}`]: {
    scope: PERMISSION_SCOPES.Brand,
  },
  [PATHS.BRAND + PATHS.CREATE]: { scope: PERMISSION_SCOPES.Brand },

  [`${PATHS.BRAND}/${PATHS.EDIT}`]: {
    scope: PERMISSION_SCOPES.Brand,
  },
  [`${PATHS.BRAND}/${PATHS.VIEW}`]: {
    scope: PERMISSION_SCOPES.Brand,
  },
  [`${PATHS.BRAND}${PATHS.BRAND_SURVEY_LIST}`]: {
    scope: PERMISSION_SCOPES.Brand,
  },
  [`${PATHS.BRAND}${PATHS.BRAND_POSTS_LIST}`]: {
    scope: PERMISSION_SCOPES.Brand,
  },
  [`${PATHS.BRAND}${PATHS.BRAND_ADMIN_LIST}/${PATHS.CREATE}`]: {
    scope: PERMISSION_SCOPES.Brand,
  },
  [`${PATHS.BRAND}${PATHS.BRAND_ADMIN_LIST}`]: {
    scope: PERMISSION_SCOPES.Brand,
  },
  [`${PATHS.BRAND}${PATHS.BRAND_ADMIN_LIST}/${PATHS.VIEW}`]: {
    scope: PERMISSION_SCOPES.Brand,
  },
  //Manage Promotions
  [`${PATHS.PROMOTION}`]: {
    scope: PERMISSION_SCOPES.MangePromotions,
  },
  [`${PATHS.PROMOTION + "/" + PATHS.CREATE}`]: {
    scope: PERMISSION_SCOPES.MangePromotions,
  },
  [`${PATHS.PROMOTION + PATHS.PROMOTION_DETAILS}`]: {
    scope: PERMISSION_SCOPES.MangePromotions,
  },
  //club reports
  [`${PATHS.CLUB_REPORTS}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.CLUB_ADMIN_REPORTS + PATHS.CLUB_USER_REPORTS}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.CLUB_ADMIN_REPORTS + PATHS.CLUB_MONITOR_REPORTS}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  //Reports
  [`${PATHS.REPORTS}/${PATHS.SUPER_USER_REPORT}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.REPORTS}/${PATHS.SUPER_CLUB_REPORT}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.REPORTS}/${PATHS.ADMIN_MONITORING}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.REPORTS}/${PATHS.SUPER_BRAND_REPORT}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.REPORTS}/${PATHS.SUPER_CLUB_REPORT}/${PATHS.CLUB_STATS_DETAILS}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.REPORTS}/${PATHS.SUPER_BRAND_REPORT}/${PATHS.BRAND_STATS_DETAILS}`]:
    {
      scope: PERMISSION_SCOPES.Reports,
    },
  [`${PATHS.BRAND_ADMIN_REPORTS}${PATHS.BRAND_MONITOR_REPORTS}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.BRAND_ADMIN_REPORTS}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  [`${PATHS.BRAND_ADMIN_REPORTS}${PATHS.BRAND_USER_REPORTS}`]: {
    scope: PERMISSION_SCOPES.Reports,
  },
  // Email Templates
  [PATHS.EMAIL_TEMPLATES]: { scope: PERMISSION_SCOPES.Email_Template },
  // Support
  [PATHS.SUPPORT]: { scope: PERMISSION_SCOPES.Support },
  // Settings
  [`${PATHS.SETTINGS}/${PATHS.GENERAL_SETTINGS}`]: {
    scope: PERMISSION_SCOPES.Settings,
  },
  [`${PATHS.SETTINGS}/${PATHS.SOCIAL_MEDIA}`]: {
    scope: PERMISSION_SCOPES.Settings,
  },
  [`${PATHS.SETTINGS}/${PATHS.PAYMENT_GATEWAY}`]: {
    scope: PERMISSION_SCOPES.Settings,
  },
  [`${PATHS.SETTINGS}/${PATHS.SMTP_SMS_DETAILS}`]: {
    scope: PERMISSION_SCOPES.Settings,
  },
  [`${PATHS.SETTINGS}/${PATHS.EMAIL_NOTIFICATIONS}`]: {
    scope: PERMISSION_SCOPES.Settings,
  },
  [`${PATHS.SETTINGS}/${PATHS.LANGUAGE_MESSAGES}`]: {
    scope: PERMISSION_SCOPES.Settings,
  },
  [`${PATHS.SETTINGS}/${PATHS.LANGAUGE_STATIC_LANG}`]: {
    scope: PERMISSION_SCOPES.Settings,
  },
  // ROle
  [PATHS.ROLE]: {
    scope: PERMISSION_SCOPES.Role,
  },
  // Profile
  [PATHS.MY_PROFILE]: {
    scope: PERMISSION_SCOPES.Profile,
  },
  [PATHS.EDIT_MY_PROFILE]: {
    scope: PERMISSION_SCOPES.Profile,
  },
  //manage points
  [`${PATHS.MANAGE_POINTS}/${PATHS.ACTIONS}`]: {
    scope: PERMISSION_SCOPES.ManagePoints,
  },
  //manage benefits
  [`${PATHS.MANAGE_BENEFITS}/${PATHS.BENEFITS}`]: {
    scope: PERMISSION_SCOPES.ManageBenefits,
  },
  [`${PATHS.MANAGE_BENEFITS}/${PATHS.BENEFITS}/${PATHS.VIEW}`]: {
    scope: PERMISSION_SCOPES.ManageBenefits,
  },
  [`${PATHS.MANAGE_BENEFITS}/${PATHS.BENEFITS}/${PATHS.CREATE}`]: {
    scope: PERMISSION_SCOPES.ManageBenefits,
  },
  //subscriber List
  [`${PATHS.SUBSCRIBERS_LIST}`]: {
    scope: PERMISSION_SCOPES.SubscribersList,
  },
};

/******************* 
  @purpose :Used for checking aside menu link permission
  @Author : INIC
  ******************/
export function ModulePermissionGate({ children, scope }) {
  const { modules } = useSelector((state) => state.permissions, shallowEqual);
  const hasPermission = useMemo(
    () =>
      !!modules?.find((module) => scope === true || module.category === scope),
    [modules]
  );

  if (hasPermission) {
    return children;
  }
  return null;
}

export const formatUrlPathScopes = (pathname, params) => {
  // remove any params from url
  const pathWithoutParams = Object.values(params).reduce(
    (path, param) => path.replace("/" + param, ""),
    pathname
  );

  // get crud scope from url (i.e view/create/edit)
  const pathScope = Object.values(AccessScopes).find(
    (scope) => pathWithoutParams.indexOf(scope.toLowerCase()) != -1
  );

  // get url module path
  const pathWithoutScope = pathScope
    ? pathWithoutParams.replace("/" + pathScope.toLowerCase(), "")
    : pathWithoutParams;

  return { pathWithoutParams, pathScope, pathWithoutScope };
};

/******************* 
  @purpose :Used for checking route permission.
            Checks any url entered in browser if it has permission
  @Author : INIC
  ******************/
export function RoutePermissionGate({ children }) {
  const { modules, isModuleLoading } = useSelector(
    (state) => state.permissions,
    shallowEqual
  );
  const { pathname } = useLocation();
  const params = useParams();
  const {
    hasModulePermission,
    pathScope,
    pathWithoutScope,
    hasScopePermission,
  } = useMemo(() => {
    // return without checking if user is super admin or api is loading
    if (isModuleLoading) {
      return {};
    }

    const { pathWithoutParams, pathScope, pathWithoutScope } =
      formatUrlPathScopes(pathname, params);
    const currentModule = modules?.find(
      (module) => module.category === routeScopes[pathWithoutScope]?.scope
    );
    let currentScope = false;
    if (pathScope) {
      currentScope = currentModule?.permissions?.find(
        (mod) =>
          mod.permissionKey ===
          `${currentModule.category
            .toLowerCase()
            .replace(/\s+/g, "_")}_${pathScope.toLowerCase()}`
      )?.isSelected;
    }

    return {
      hasModulePermission: !!currentModule,
      hasScopePermission: !!currentScope,
      pathWithoutScope,
      pathWithoutParams,
      pathScope,
    };
  }, [modules, pathname]);

  if (isModuleLoading) {
    return <SuspenseLoader />;
  }

  // if user does not have permission navigate to dashboard
  if (!hasModulePermission && pathWithoutScope !== PATHS.DASHBOARD) {
    return <Navigate to={PATHS.DASHBOARD} />;
  }

  // if user have module permission but does not have crud operation permission
  // then navigate to back to module
  if (pathScope && hasModulePermission && !hasScopePermission) {
    return <Navigate to={pathWithoutScope} replace />;
  }

  return children;
}

export function ScopePermissionGate({ children, scope, module }) {
  const { isModuleLoading, formatedModulesData } = useSelector(
    (state) => state.permissions,
    shallowEqual
  );

  const hasPermission = useMemo(
    () =>
      !!(
        formatedModulesData?.[module]?.[`${scope}`] ||
        formatedModulesData?.[module]?.allScope
      ),
    [formatedModulesData]
  );
  if (isModuleLoading) {
    return <></>;
  }

  if (!hasPermission) {
    return <></>;
  }

  return children;
}
